// Tier access rules for streamer events
const STREAMER_TIER_ACCESS = {
  2: { // Prime Plus
    allowXp: true,
    allowBarks: true,
    allowedPacks: ['prime'],
    allowedRandomCards: [1], // Standard only
    allowedEventTypes: ['custom'],
    allowFrames: false,
    allowBackgrounds: false,
    allowChests: false,
    allowOwnedShopItems: false
  },
  3: { // Ultra Plus
    allowXp: true,
    allowBarks: true,
    allowedPacks: ['prime', 'ultra'],
    allowedRandomCards: [1, 2], // Standard, Ultra
    allowedEventTypes: ['custom', 'quest'],
    allowFrames: true,
    allowBackgrounds: false,
    allowChests: true,
    allowOwnedShopItems: true
  },
  4: { // Legendary Plus
    allowXp: true,
    allowBarks: true,
    allowedPacks: ['prime', 'ultra', 'legendary'],
    allowedRandomCards: [1, 2, 3, 4, 5, 6], // All types
    allowedEventTypes: ['custom', 'quest', 'tournament'],
    allowFrames: true,
    allowBackgrounds: true,
    allowChests: true,
    allowOwnedShopItems: true
  }
};

Vue.component('streamer-events', {
  template: templates.streamerEvents,
  data() {
    return {
      model: {
        edit: false,
        hasSubscription: false,
        isLoading: true,
        streamerTier: 0,
        minRequiredTier: 2, // Default, will be loaded from settings
        tierAccess: {},
        expandedRow: '',
        inventory: {
          prime: 0,
          ultra: 0,
          legendary: 0,
          chest: 0
        },
        originalTotals: {}, // Store original API totals for buildStreamerPacks
        streamerPacks: [],
        chests: {},
        ownedShopItems: {
          frames: {},
          backgrounds: {}
        },
        ownedExclusiveCards: [],
        event: {
          name: null,
          description: null,
          from: null,
          to: null,
          custom: {
            battleOption: '',
            card_amount: 0,
            wins: 0,
            attempt: 0,
            level: 0,
            retry: 0,
            cards: [{ cardId: null, tier: 0 }],
            temporaryCards: [],
            selectedMode: '',
            cardTeam: false,
            cardManagementMode: 'existing',
            teamName: ''
          },
          quest: null,
          tournament: null,
          rewards: {
            xp: 0,
            barks: 0,
            givePack: [{ packId: null, packs_quantity: 0 }],
            exclusiveCard: [{ cardId: null, card_quantity: 0 }],
            giveChest: [{ chestId: null, chest_quantity: 0 }],
            giveShopItem: [{ itemId: null, itemType: null }],
            packs_amount: 0,
            exclusive_cards_amount: 0,
            chests_amount: 0,
            shop_items_amount: 0
          }
        },
        events: [],
        cards: {},
        eventCards: {},
        types: {},
        abilities: {},
        traits: [],
        packs: {},
        frames: {},
        backgrounds: {},
        quests: {},
        tournaments: {},
        subscriptions: {},
        battle_options: [
          { value: 'single', name: 'Single Battle' },
          { value: '2v2', name: '2v2' },
          { value: '3v3', name: '3v3' },
          { value: '4v4', name: '4v4' }
        ],
        randomcard: [
          { name: '-- Select Card Type --' },
          { name: 'Standard Card' },
          { name: 'Ultra Card' },
          { name: 'Legendary Card' },
          { name: 'Ultimate Card' },
          { name: 'Bone Card' },
          { name: 'Ascension Card' }
        ],
        isCustomEnabled: true,
        isQuestEnabled: false,
        isTournamentEnabled: false,
        existingQuest: false,
        hasData: false
      }
    };
  },
  computed: {
    // Filter packs based on inventory and tier
    availablePacks() {
      const c = this;
      if (!c.model.tierAccess.allowedPacks) return {};

      const filtered = {};
      for (const [key, pack] of Object.entries(c.model.packs)) {
        const packType = c.getPackType(pack.name);
        if (c.model.tierAccess.allowedPacks.includes(packType)) {
          // Check if there's inventory for this pack type
          if (c.model.inventory[packType] > 0 || packType === 'chest') {
            filtered[key] = pack;
          }
        }
      }
      return filtered;
    },

    // Filter random cards based on tier
    availableRandomCards() {
      const c = this;
      if (!c.model.tierAccess.allowedRandomCards) return [];

      return c.model.randomcard.filter((card, index) =>
        index === 0 || c.model.tierAccess.allowedRandomCards.includes(index)
      );
    },

    // Check if can create quest events
    canCreateQuest() {
      return this.model.tierAccess.allowedEventTypes?.includes('quest') ?? false;
    },

    // Check if can create tournament events
    canCreateTournament() {
      return this.model.tierAccess.allowedEventTypes?.includes('tournament') ?? false;
    },

    // Check if can give chests as rewards
    canGiveChests() {
      return this.model.tierAccess.allowChests && this.model.inventory.chest > 0;
    },

    // Check if can give owned shop items as rewards
    canGiveShopItems() {
      return this.model.tierAccess.allowOwnedShopItems &&
        (Object.keys(this.model.ownedShopItems.frames).length > 0 ||
         Object.keys(this.model.ownedShopItems.backgrounds).length > 0);
    },

    // Get available chests from inventory
    availableChests() {
      if (!this.model.tierAccess.allowChests) return {};
      return this.model.chests;
    },

    // Get owned shop items (frames and backgrounds the streamer owns)
    availableShopItems() {
      if (!this.model.tierAccess.allowOwnedShopItems) return [];

      const items = [];

      // Add owned frames
      for (const [id, frame] of Object.entries(this.model.ownedShopItems.frames)) {
        items.push({
          id: id,
          type: 'frame',
          name: frame.name,
          image: frame.frameImage,
          displayName: `${frame.name} [Frame]`
        });
      }

      // Add owned backgrounds
      for (const [id, bg] of Object.entries(this.model.ownedShopItems.backgrounds)) {
        items.push({
          id: id,
          type: 'background',
          name: bg.name,
          image: bg.backgroundImage,
          displayName: `${bg.name} [Background]`
        });
      }

      return items;
    },

    // Get tier name for display
    tierName() {
      const tierNames = {
        2: 'Prime Plus',
        3: 'Ultra Plus',
        4: 'Legendary Plus'
      };
      return tierNames[this.model.streamerTier] || 'Unknown';
    }
  },
  mounted() {
    const c = this;
    c.getStreamerTier();
  },
  methods: {
    async getStreamerTier() {
      const c = this;
      try {
        // Fetch minimum tier setting from admin settings
        try {
          const settingsResponse = await settingsService.getByKey({ key: 'streamer_event_min_tier' });
          if (settingsResponse.data && settingsResponse.data.length > 0) {
            c.model.minRequiredTier = parseInt(settingsResponse.data[0].value) || 2;
          }
        } catch (settingsError) {
          console.warn('Could not load min tier setting, using default:', settingsError);
        }

        const response = await packService.getStreamerPacks();
      

        if (response.data.subscriptions?.subscriptions?.length > 0) {
          c.model.hasSubscription = true;
          c.model.subscriptions = response.data.subscriptions.subscriptions;

          // Get pack inventory
          const totals = response.data.totals || {};
          c.model.originalTotals = totals; // Store original for buildStreamerPacks
          c.model.inventory = {
            prime: totals.standard || totals.prime || 0,
            ultra: totals.ultra || 0,
            legendary: totals.legendary || 0,
            chest: (totals.primeChest || 0) + (totals.ultraChest || 0) + (totals.legendaryChest || 0)
          };

          // Determine tier from subscription
          const subscription = c.model.subscriptions[0];
          c.model.streamerTier = c.determineTier(subscription);

          // Check if streamer meets minimum tier requirement
          if (c.model.streamerTier < c.model.minRequiredTier) {
            c.model.hasSubscription = false;
            const tierNames = { 2: 'Prime Plus', 3: 'Ultra Plus', 4: 'Legendary Plus' };
            utils.alert.error(`${tierNames[c.model.minRequiredTier] || 'Higher tier'} subscription or above is required to create events`);
            return;
          }

          c.model.tierAccess = STREAMER_TIER_ACCESS[c.model.streamerTier] || STREAMER_TIER_ACCESS[2];

          // Load data after tier is determined
          c.get();
          c.getCards();
          c.getAbilities();
          c.getTypes();
          c.getTraits();
          c.getPacks();

          if (c.model.tierAccess.allowFrames) {
            c.getFrames();
          }
          if (c.model.tierAccess.allowBackgrounds) {
            c.getBackgrounds();
          }
          if (c.model.tierAccess.allowedEventTypes?.includes('tournament')) {
            c.getTournaments();
          }
          if (c.model.tierAccess.allowChests) {
            c.getChests();
          }
          if (c.model.tierAccess.allowOwnedShopItems) {
            c.getOwnedShopItems();
          }
          c.getOwnedExclusiveCards();
        } else {
          c.model.hasSubscription = false;
          utils.alert.error('Active subscription required to create events');
        }
      } catch (error) {
        console.error('Error fetching streamer tier:', error);
        c.model.hasSubscription = false;
      } finally {
        c.model.isLoading = false;
      }
    },

    determineTier(subscription) {
      // Determine tier from subscription data
      // The tier field should be 2, 3, or 4
      if (subscription.tier) {
        return parseInt(subscription.tier);
      }
      // Fallback: check subscription name or ID
      if (subscription.subscriptionId) {
        const subId = parseInt(subscription.subscriptionId);
        if (subId === 3) return 4; // Legendary
        if (subId === 2) return 3; // Ultra
      }
      return 2; // Default to Prime
    },

    getPackType(packName) {
      if (!packName) return 'prime';
      const name = packName.toLowerCase();
      if (name.includes('legendary')) return 'legendary';
      if (name.includes('ultra')) return 'ultra';
      return 'prime';
    },

    // Build streamerPacks structure from inventory and packs catalog
    buildStreamerPacks(totals) {
      const c = this;
      const packTypes = { 'Prime': [], 'Ultra': [], 'Legendary': [], 'Chest': [] };

      // Helper to check if pack is valid for streamer (isChest=0, streamerShow=1, active=1)
      const isValidPack = (pack) => {
        if (pack.isChest === 1 || pack.isChest === true) return false;
        if (pack.streamerShow !== 1 && pack.streamerShow !== true) return false;
        if (pack.active !== 1 && pack.active !== true) return false;
        return true;
      };

      // Helper to check if chest is valid (isChest=1, streamerShow=1, active=1, deleted=0)
      const isValidChest = (pack) => {
        if (pack.isChest !== 1 && pack.isChest !== true) return false;
        if (pack.streamerShow !== 1 && pack.streamerShow !== true) return false;
        if (pack.active !== 1 && pack.active !== true) return false;
        if (pack.deleted === 1 || pack.deleted === true) return false;
        return true;
      };

      // Helper to get pack type from pack.type field or fallback to name parsing
      const getPackCategory = (pack) => {
        // Use pack.type field if available
        if (pack.type) {
          const packType = pack.type.toLowerCase();
          if (packType === 'prime' || packType === 'standard') return 'Prime';
          if (packType === 'ultra') return 'Ultra';
          if (packType === 'legendary') return 'Legendary';
        }
        // Fallback to name parsing
        const packName = (pack.name || '').toLowerCase();
        if (packName.includes('legendary')) return 'Legendary';
        if (packName.includes('ultra')) return 'Ultra';
        if (packName.includes('prime')) return 'Prime';
        return 'Prime'; // Default to Prime if unknown
      };

      // Group packs by type
      for (let packId in c.model.packs) {
        const pack = c.model.packs[packId];

        // Check if it's a valid chest first
        if (isValidChest(pack)) {
          packTypes['Chest'].push(packId);
          continue;
        }

        // Check if it's a valid regular pack
        if (isValidPack(pack)) {
          const category = getPackCategory(pack);
          if (packTypes[category]) {
            packTypes[category].push(packId);
          }
        }
      }

      // Create streamerPacks array with pack IDs
      c.model.streamerPacks = [
        { type: 'Prime', count: totals.standard || totals.prime || 0, packIds: packTypes['Prime'] },
        { type: 'Ultra', count: totals.ultra || 0, packIds: packTypes['Ultra'] },
        { type: 'Legendary', count: totals.legendary || 0, packIds: packTypes['Legendary'] },
        { type: 'Chest', count: (totals.primeChest || 0) + (totals.ultraChest || 0) + (totals.legendaryChest || 0), packIds: packTypes['Chest'] }
      ];

    },

    get() {
      const c = this;
      let data = {};

      streamerEventsService.get()
        .then(function (response) {
          for (let d of response.data) {
            data[`${d.id}`] = d;
          }
          c.model.events = data;
        })
        .catch(function (error) {
          console.error('Error fetching events:', error);
        });
    },

    getCards() {
      const c = this;
      let data = {};

      cardService.get()
        .then(function (response) {
          for (let d of response.data) {
            data[`${d.id}`] = d;
          }
          c.model.cards = data;
        })
        .catch(function (error) {
          console.error('Error fetching cards:', error);
        });
    },

    getTypes() {
      const c = this;
      let data = {};

      typeService.get()
        .then(function (response) {
          for (let d of response.data) {
            data[`${d.id}`] = d;
          }
          c.model.types = data;
        })
        .catch(function (error) {
          console.error('Error fetching types:', error);
        });
    },

    getAbilities() {
      const c = this;
      let data = {};

      abilityService.get()
        .then(function (response) {
          for (let d of response.data) {
            data[`${d.id}`] = d;
          }
          c.model.abilities = data;
        })
        .catch(function (error) {
          console.error('Error fetching abilities:', error);
        });
    },

    getTraits() {
      const c = this;

      traitService.get()
        .then(function (response) {
          c.model.traits = response.data;
        })
        .catch(function (error) {
          console.error('Error fetching traits:', error);
        });
    },

    getPacks() {
      const c = this;
      let data = {};

      packService.get()
        .then(function (response) {
          for (let d of response.data) {
            data[`${d.id}`] = d;
          }
          c.model.packs = data;

          // Build streamerPacks after packs are loaded
          if (c.model.originalTotals) {
            c.buildStreamerPacks(c.model.originalTotals);
          }
        })
        .catch(function (error) {
          console.error('Error fetching packs:', error);
        });
    },

    getFrames() {
      const c = this;
      let data = {};

      frameService.get()
        .then(function (response) {
          for (let d of response.data) {
            d.frameImage = d.uploaded && d.extension
              ? `${root}/images/frames/${d.id}/${d.id}.${d.extension}?t=${Date.now()}`
              : null;
            data[`${d.id}`] = d;
          }
          c.model.frames = data;
        })
        .catch(function (error) {
          console.error('Error loading frames:', error);
        });
    },

    getBackgrounds() {
      const c = this;
      let data = {};

      backgroundService.get()
        .then(function (response) {
          for (let d of response.data) {
            d.backgroundImage = d.extension
              ? `${root}/images/backgrounds/${d.id}/${d.id}.${d.extension}?t=${Date.now()}`
              : null;
            data[`${d.id}`] = d;
          }
          c.model.backgrounds = data;
        })
        .catch(function (error) {
          console.error('Error loading backgrounds:', error);
        });
    },

    getTournaments() {
      const c = this;
      let data = {};

      tournamentService.get()
        .then(function (response) {
          for (let d of response.data) {
            data[`${d.id}`] = d;
          }
          c.model.tournaments = data;
        })
        .catch(function (error) {
          console.error('Error fetching tournaments:', error);
        });
    },

    getChests() {
      const c = this;
      let data = {};

      packService.get()
        .then(function (response) {
          // Filter for chests: isChest=1, streamerShow=1, active=1, deleted=0
          const chests = response.data.filter(pack =>
            (pack.isChest === 1 || pack.isChest === true) &&
            (pack.streamerShow === 1 || pack.streamerShow === true) &&
            (pack.active === 1 || pack.active === true) &&
            (pack.deleted === 0 || pack.deleted === false || !pack.deleted)
          );

          // Helper to get chest quantity based on type/name
          const getChestQuantity = (chest) => {
            const totals = c.model.originalTotals || {};
            const name = (chest.name || '').toLowerCase();
            const type = (chest.type || '').toLowerCase();

            if (name.includes('legendary') || type.includes('legendary')) {
              return totals.legendaryChest || 0;
            }
            if (name.includes('ultra') || type.includes('ultra')) {
              return totals.ultraChest || 0;
            }
            // Default to prime/standard chest
            return totals.primeChest || 0;
          };

          for (let chest of chests) {
            data[`${chest.id}`] = {
              ...chest,
              availableQuantity: getChestQuantity(chest)
            };
          }
          c.model.chests = data;
        })
        .catch(function (error) {
          console.error('Error loading chests:', error);
        });
    },

    getOwnedShopItems() {
      const c = this;
      const twitchId = utils.cookies.get('user_id');

      if (!twitchId) return;

      // Fetch owned frames
      rewardFrameService.getShopItems(twitchId)
        .then(function (response) {
          if (response.success && response.data) {
            const frames = {};
            for (let frame of response.data) {
              // Only include claimed (owned) items
              if (frame.is_claimed) {
                frames[`${frame.id}`] = {
                  ...frame,
                  frameImage: `${root}/${frame.frame_image_url}`
                };
              }
            }
            c.model.ownedShopItems.frames = frames;
          }
        })
        .catch(function (error) {
          console.error('Error loading owned frames:', error);
        });

      // Fetch owned backgrounds
      rewardBackgroundService.getShopItems(twitchId)
        .then(function (response) {
          if (response.success && response.data) {
            const backgrounds = {};
            for (let bg of response.data) {
              // Only include claimed (owned) items
              if (bg.is_claimed) {
                backgrounds[`${bg.id}`] = {
                  ...bg,
                  backgroundImage: `${root}/${bg.background_image_url}`
                };
              }
            }
            c.model.ownedShopItems.backgrounds = backgrounds;
          }
        })
        .catch(function (error) {
          console.error('Error loading owned backgrounds:', error);
        });
    },

    getOwnedExclusiveCards() {
      const c = this;
      const twitchId = utils.cookies.get('user_id');

      if (!twitchId) return;

      exclusiveCardService.getUserCards(twitchId)
        .then(function (response) {
          if (response.data) {
            const formattedCards = {};
            for (let card of response.data) {
              formattedCards[`${card.cardId}`] = {
                id: card.cardId,
                cardId: card.cardId,
                name: card.name,
                hp: card.hp,
                attack: card.attack,
                defence: card.defence,
                speed: card.speed,
                accuracy: card.accuracy,
                type: card.type,
                ability: card.ability,
                tier: card.tier,
                exclusiveUntil: card.exclusiveUntil,
                dateRedeemed: card.dateRedeemed,
                quantity: card.quantity || 1,
                isExclusive: true
              };
            }
            c.model.ownedExclusiveCards = formattedCards;
          }
        })
        .catch(function (error) {
          console.error('Error loading owned exclusive cards:', error);
        });
    },

    add() {
      const c = this;
      c.model.edit = true;
    },

    reset() {
      const c = this;
      c.model.edit = false;
      c.model.event = {
        name: null,
        description: null,
        from: null,
        to: null,
        custom: {
          battleOption: '',
          card_amount: 0,
          wins: 0,
          attempt: 0,
          level: 0,
          retry: 0,
          cards: [{ cardId: null, tier: 0 }],
          temporaryCards: [],
          selectedMode: '',
          cardTeam: false,
          cardManagementMode: 'existing',
          teamName: ''
        },
        quest: null,
        tournament: null,
        rewards: {
          xp: 0,
          barks: 0,
          givePack: [{ packId: null, packs_quantity: 0 }],
          exclusiveCard: [{ cardId: null, card_quantity: 0 }],
          giveChest: [{ chestId: null, chest_quantity: 0 }],
          giveShopItem: [{ itemId: null, itemType: null }],
          packs_amount: 0,
          exclusive_cards_amount: 0,
          chests_amount: 0,
          shop_items_amount: 0
        }
      };
      c.model.isCustomEnabled = true;
      c.model.isQuestEnabled = false;
      c.model.isTournamentEnabled = false;
      c.model.hasData = false;
    },

    validateRewardsForTier() {
      const c = this;
      const rewards = c.model.event.rewards;
      const tierAccess = c.model.tierAccess;

      // Validate pack types against tier and inventory
      for (const pack of rewards.givePack) {
        if (pack.packId) {
          const packData = c.model.packs[pack.packId];
          if (packData) {
            const packType = c.getPackType(packData.name);

            // Check if pack type is allowed for this tier
            if (!tierAccess.allowedPacks.includes(packType)) {
              utils.alert.error(`${packType.charAt(0).toUpperCase() + packType.slice(1)} packs are not available at your tier`);
              return false;
            }

            // Check inventory
            const quantity = parseInt(pack.packs_quantity) || 0;
            if (quantity > c.model.inventory[packType]) {
              utils.alert.error(`Not enough ${packType} packs in inventory. Available: ${c.model.inventory[packType]}`);
              return false;
            }
          }
        }
      }

      // Validate frames/backgrounds access
      if (rewards.rewardFrames && rewards.rewardFrames.length > 0) {
        const hasFrames = rewards.rewardFrames.some(r => r.type === 'frame');
        const hasBackgrounds = rewards.rewardFrames.some(r => r.type === 'background');

        if (hasFrames && !tierAccess.allowFrames) {
          utils.alert.error('Frames are not available at your tier');
          return false;
        }
        if (hasBackgrounds && !tierAccess.allowBackgrounds) {
          utils.alert.error('Backgrounds are not available at your tier');
          return false;
        }
      }

      // Validate chest rewards
      if (rewards.giveChest) {
        for (const chest of rewards.giveChest) {
          if (chest.chestId) {
            if (!tierAccess.allowChests) {
              utils.alert.error('Chests are not available at your tier');
              return false;
            }

            // Check chest inventory
            const quantity = parseInt(chest.chest_quantity) || 0;
            if (quantity > c.model.inventory.chest) {
              utils.alert.error(`Not enough chests in inventory. Available: ${c.model.inventory.chest}`);
              return false;
            }
          }
        }
      }

      // Validate owned shop items
      if (rewards.giveShopItem) {
        for (const item of rewards.giveShopItem) {
          if (item.itemId) {
            if (!tierAccess.allowOwnedShopItems) {
              utils.alert.error('Shop item rewards are not available at your tier');
              return false;
            }

            // Check if item is actually owned
            const isOwnedFrame = c.model.ownedShopItems.frames[item.itemId];
            const isOwnedBackground = c.model.ownedShopItems.backgrounds[item.itemId];

            if (!isOwnedFrame && !isOwnedBackground) {
              utils.alert.error('You can only give shop items that you own');
              return false;
            }
          }
        }
      }

      return true;
    },

    save() {
      const c = this;
      const data = c.model.event;

      // Basic validation
      if (!data.name) {
        utils.alert.error('Event name is required');
        return;
      }

      if (!data.description) {
        utils.alert.error('Event description is required');
        return;
      }

      if (!data.from) {
        utils.alert.error('Start date is required');
        return;
      }

      if (!data.to) {
        utils.alert.error('End date is required');
        return;
      }

      // Validate custom event if enabled
      if (c.model.isCustomEnabled && data.custom) {
        if (!data.custom.battleOption) {
          utils.alert.error('Select battle options');
          return;
        }

        if (data.custom.wins === 0) {
          utils.alert.error('Wins is required');
          return;
        }

        if (!data.custom.selectedMode) {
          utils.alert.error('Select mode');
          return;
        }
      }

      // Validate tournament event if enabled
      if (c.model.isTournamentEnabled && data.tournament) {
        if (!data.tournament.tournamentId) {
          utils.alert.error('Select a tournament');
          return;
        }
      }

      // Validate at least one reward
      const rewards = data.rewards;
      const hasXpOrBarks = Number(rewards.xp) > 0 || Number(rewards.barks) > 0;
      const hasPack = rewards.givePack && rewards.givePack[0]?.packId !== null;
      const hasExclusiveCard = rewards.exclusiveCard && rewards.exclusiveCard[0]?.cardId !== null;
      const hasChest = rewards.giveChest && rewards.giveChest[0]?.chestId !== null;
      const hasShopItem = rewards.giveShopItem && rewards.giveShopItem[0]?.itemId !== null;

      if (!hasXpOrBarks && !hasPack && !hasExclusiveCard && !hasChest && !hasShopItem) {
        utils.alert.error('Give at least 1 reward');
        return;
      }

      // Validate rewards against tier limits
      if (!c.validateRewardsForTier()) {
        return;
      }

      streamerEventsService.save(data)
        .then(function (response) {
          if (response.data.status === 400) {
            utils.alert.error(response.data.message);
            return;
          }
          c.reset();
          c.get();
          c.getStreamerTier(); // Refresh inventory
          utils.alert.success('Event saved!');
        })
        .catch(function (error) {
          utils.alert.error('Failed to save event.');
        });
    },

    edit(id) {
      const c = this;
      let data = c.model.events[`${id}`];

      if (id == c.model.events.id) {
        c.model.edit = false;
      } else {
        c.model.edit = true;
      }

      if (data.quest) {
        c.model.isQuestEnabled = true;
        c.model.isCustomEnabled = false;
        c.model.isTournamentEnabled = false;
        c.model.hasData = true;
      }
      if (data.custom) {
        c.model.isQuestEnabled = false;
        c.model.isCustomEnabled = true;
        c.model.isTournamentEnabled = false;
        c.model.hasData = true;
        if (!data.custom.cards) {
          data.custom.cards = [{ cardId: null, tier: 0 }];
        }
      }
      if (data.tournament) {
        c.model.isTournamentEnabled = true;
        c.model.isQuestEnabled = false;
        c.model.isCustomEnabled = false;
        c.model.hasData = true;
      }

      c.model.event = {
        id: data.id,
        name: data.name,
        description: data.description,
        from: data.from,
        to: data.to,
        quest: data.quest,
        custom: data.custom,
        tournament: data.tournament,
        rewards: data.rewards
      };

      // Initialize rewards if not exists
      if (!c.model.event.rewards.rewardFrames) {
        c.model.event.rewards.rewardFrames = [];
      }
      if (!c.model.event.rewards.rewardFrameMode) {
        c.model.event.rewards.rewardFrameMode = 'single-frame';
      }
    },

    inputValue(event) {
      const c = this;

      if (event === 'quest' && c.canCreateQuest) {
        c.model.isQuestEnabled = true;
        c.model.isCustomEnabled = false;
        c.model.isTournamentEnabled = false;
        c.model.hasData = true;
        c.loadQuest();
      } else if (event === 'tournament' && c.canCreateTournament) {
        c.model.isTournamentEnabled = true;
        c.model.isQuestEnabled = false;
        c.model.isCustomEnabled = false;
        c.model.hasData = true;
        c.loadTournament();
      } else {
        c.model.isQuestEnabled = false;
        c.model.isCustomEnabled = true;
        c.model.isTournamentEnabled = false;
        c.model.hasData = false;
        c.loadCustom();
      }
    },

    loadCustom() {
      const c = this;
      c.model.event.custom = {
        battleOption: '',
        card_amount: 0,
        wins: 0,
        attempt: 0,
        level: 0,
        retry: 0,
        cards: [{ cardId: null, tier: 0 }],
        temporaryCards: [],
        selectedMode: '',
        cardTeam: false,
        cardManagementMode: 'existing',
        teamName: ''
      };
    },

    loadQuest() {
      const c = this;
      c.model.event.quest = {
        name: null,
        description: null,
        cards: [{ cardId: null, tier: 0 }],
        date: null,
        time: null,
        level: 0,
        card_amount: 0,
        selectedMode: null,
        wins: 0
      };
    },

    loadTournament() {
      const c = this;
      c.model.event.tournament = {
        tournamentId: null
      };
    },

    handleRewardChange(rewardData) {
      const c = this;
      c.model.event.rewards = { ...c.model.event.rewards, ...rewardData };
    },

    handleQuestChange(data) {
      const c = this;
      if (data.model) {
        Object.keys(data.model).forEach(key => {
          if (Array.isArray(data.model[key])) {
            c.model.event.quest[key] = [...data.model[key]];
          } else if (typeof data.model[key] === 'object' && data.model[key] !== null) {
            c.model.event.quest[key] = { ...c.model.event.quest[key], ...data.model[key] };
          } else {
            c.model.event.quest[key] = data.model[key];
          }
        });
      }
    },

    incrementCards() {
      const c = this;
      c.model.event.custom.card_amount++;
      c.model.event.custom.cards.push({ cardId: null, tier: 0 });
    },

    removeCard(index) {
      const c = this;
      if (c.model.event.custom.card_amount > 0) {
        c.model.event.custom.card_amount--;
        c.model.event.custom.cards.splice(index, 1);
      }
    },

    handleCardModeChange(mode) {
      const c = this;
      c.model.event.custom.cardManagementMode = mode;

      if (mode === 'existing') {
        c.model.event.custom.temporaryCards = [];
      } else {
        c.model.event.custom.cards = [{ cardId: null, tier: 0 }];
      }
    },

    handleExistingCardsUpdate(cards) {
      const c = this;
      c.model.event.custom.cards = cards;
      c.model.event.custom.card_amount = cards.length;
    },

    handleTemporaryCardsUpdate(temporaryCards) {
      const c = this;
      c.model.event.custom.temporaryCards = temporaryCards;
    },

    generateCardTeam(event) {
      const c = this;
      c.model.isTeam = event;
    },

    formattedDate(eventDate) {
      const date = new Date(eventDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    },

    toggleDetails(index) {
      const c = this;
      c.model.expandedRow = c.model.expandedRow === index ? null : index;
    },

    getEventType(event) {
      if (event.custom) {
        return 'Custom';
      } else if (event.quest) {
        return 'Quest';
      } else if (event.tournament) {
        return 'Tournament';
      }
      return 'Event';
    },

    battleRules(rules) {
      if (rules === 'to_defeat') {
        return 'To Defeat';
      } else if (rules === 'to_win') {
        return 'To Win';
      } else if (rules === 'both' || rules === 'any') {
        return 'Win/Defeat';
      } else if (rules === 'battle') {
        return 'Battle';
      }
      return 'Battle';
    }
  }
});
